import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, RefreshCw, RotateCw, Trash2, Upload } from "lucide-react";

interface TestCode {
  id: string;
  username: string;
  password: string;
  server_url: string;
  activation_url: string;
  status: string;
  assigned_to_email: string | null;
  assigned_at: string | null;
  activated_at: string | null;
  expires_at: string | null;
  created_at: string;
}

// Extract username and password from URL like http://mawap.online/get.php?username=940669566122389&password=1593574628&type=simple&output=ts
const parseActivationUrl = (url: string): { username: string; password: string; serverUrl: string } | null => {
  try {
    const parsed = new URL(url.trim());
    const username = parsed.searchParams.get("username");
    const password = parsed.searchParams.get("password");
    if (!username || !password) return null;
    return {
      username,
      password,
      serverUrl: `${parsed.protocol}//${parsed.host}`,
    };
  } catch {
    return null;
  }
};

const AdminTestCodesSection = () => {
  const [codes, setCodes] = useState<TestCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [bulkUrls, setBulkUrls] = useState("");
  const [singleUrl, setSingleUrl] = useState("");
  const [adding, setAdding] = useState(false);

  const availableCount = codes.filter((c) => c.status === "available").length;
  const activeCount = codes.filter((c) => c.status === "active").length;
  const expiredCount = codes.filter((c) => c.status === "expired").length;

  useEffect(() => {
    fetchCodes();
  }, []);

  const fetchCodes = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("test_codes")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      setCodes((data as TestCode[]) || []);
    } catch (error) {
      console.error("Error fetching test codes:", error);
      toast.error("Erreur lors du chargement des codes");
    } finally {
      setLoading(false);
    }
  };

  const handleAddSingle = async () => {
    if (!singleUrl.trim()) {
      toast.error("Collez une URL d'activation");
      return;
    }

    const parsed = parseActivationUrl(singleUrl);
    if (!parsed) {
      toast.error("URL invalide. Format attendu : http://mawap.online/get.php?username=...&password=...");
      return;
    }

    setAdding(true);
    try {
      const { error } = await supabase.from("test_codes").insert({
        username: parsed.username,
        password: parsed.password,
        server_url: parsed.serverUrl,
        activation_url: singleUrl.trim(),
        status: "available",
      });

      if (error) throw error;
      toast.success("Code ajouté !");
      setSingleUrl("");
      fetchCodes();
    } catch (error) {
      console.error("Error adding code:", error);
      toast.error("Erreur lors de l'ajout");
    } finally {
      setAdding(false);
    }
  };

  const handleAddBulk = async () => {
    const urls = bulkUrls
      .split("\n")
      .map((u) => u.trim())
      .filter((u) => u.length > 0);

    if (urls.length === 0) {
      toast.error("Collez au moins une URL");
      return;
    }

    const parsedCodes = urls.map((url) => {
      const parsed = parseActivationUrl(url);
      if (!parsed) return null;
      return {
        username: parsed.username,
        password: parsed.password,
        server_url: parsed.serverUrl,
        activation_url: url,
        status: "available",
      };
    });

    const validCodes = parsedCodes.filter((c) => c !== null);
    const invalidCount = parsedCodes.length - validCodes.length;

    if (validCodes.length === 0) {
      toast.error("Aucune URL valide trouvée");
      return;
    }

    setAdding(true);
    try {
      const { error } = await supabase.from("test_codes").insert(validCodes);

      if (error) throw error;
      toast.success(`${validCodes.length} code(s) ajouté(s)${invalidCount > 0 ? ` (${invalidCount} URL(s) invalide(s) ignorée(s))` : ""}`);
      setBulkUrls("");
      fetchCodes();
    } catch (error) {
      console.error("Error adding codes:", error);
      toast.error("Erreur lors de l'ajout en masse");
    } finally {
      setAdding(false);
    }
  };

  const handleRecycle = async (codeId: string) => {
    try {
      const { error } = await supabase
        .from("test_codes")
        .update({
          status: "available",
          assigned_to_email: null,
          assigned_at: null,
          activated_at: null,
          expires_at: null,
        })
        .eq("id", codeId);

      if (error) throw error;
      toast.success("Code recyclé — remis en disponible");
      fetchCodes();
    } catch (error) {
      console.error("Error recycling code:", error);
      toast.error("Erreur lors du recyclage");
    }
  };

  const handleDelete = async (codeId: string) => {
    try {
      const { error } = await supabase.from("test_codes").delete().eq("id", codeId);
      if (error) throw error;
      toast.success("Code supprimé");
      fetchCodes();
    } catch (error) {
      console.error("Error deleting code:", error);
      toast.error("Erreur lors de la suppression");
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "available":
        return <Badge className="bg-green-500/20 text-green-400 border-green-500/30">Disponible</Badge>;
      case "active":
        return <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">Actif</Badge>;
      case "expired":
        return <Badge className="bg-red-500/20 text-red-400 border-red-500/30">Expiré</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="space-y-6">
      {/* Compteurs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-card border-border">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-green-400">{availableCount}</p>
            <p className="text-sm text-muted-foreground">Disponibles</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-blue-400">{activeCount}</p>
            <p className="text-sm text-muted-foreground">Actifs</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-red-400">{expiredCount}</p>
            <p className="text-sm text-muted-foreground">Expirés</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-foreground">{codes.length}</p>
            <p className="text-sm text-muted-foreground">Total</p>
          </CardContent>
        </Card>
      </div>

      {/* Ajout en masse */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="text-foreground flex items-center gap-2">
            <Upload className="w-5 h-5" />
            Ajouter des codes en masse
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Textarea
            placeholder={"Collez vos URLs d'activation (une par ligne) :\nhttp://mawap.online/get.php?username=940669566122389&password=1593574628&type=simple&output=ts\nhttp://mawap.online/get.php?username=...&password=...&type=simple&output=ts"}
            value={bulkUrls}
            onChange={(e) => setBulkUrls(e.target.value)}
            rows={5}
            className="font-mono text-xs"
          />
          <Button onClick={handleAddBulk} disabled={adding || !bulkUrls.trim()}>
            <Plus className="w-4 h-4 mr-2" />
            Ajouter {bulkUrls.split("\n").filter((u) => u.trim()).length} URL(s)
          </Button>
        </CardContent>
      </Card>

      {/* Ajout unitaire */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="text-foreground flex items-center gap-2">
            <Plus className="w-5 h-5" />
            Ajouter un code
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Input
              placeholder="http://mawap.online/get.php?username=...&password=...&type=simple&output=ts"
              value={singleUrl}
              onChange={(e) => setSingleUrl(e.target.value)}
              className="font-mono text-xs flex-1"
            />
            <Button onClick={handleAddSingle} disabled={adding || !singleUrl.trim()}>
              <Plus className="w-4 h-4 mr-2" />
              Ajouter
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tableau des codes */}
      <Card className="bg-card border-border">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-foreground">
            Codes de test ({codes.length})
          </CardTitle>
          <Button variant="outline" size="sm" onClick={fetchCodes}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualiser
          </Button>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Chargement...</div>
          ) : codes.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              Aucun code de test. Ajoutez-en ci-dessus.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-border">
                    <TableHead>Statut</TableHead>
                    <TableHead>Username</TableHead>
                    <TableHead>Password</TableHead>
                    <TableHead>Serveur</TableHead>
                    <TableHead>Attribué à</TableHead>
                    <TableHead>Date attribution</TableHead>
                    <TableHead>Expire</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {codes.map((code) => (
                    <TableRow key={code.id} className="border-border">
                      <TableCell>{getStatusBadge(code.status)}</TableCell>
                      <TableCell className="font-mono text-xs">{code.username}</TableCell>
                      <TableCell className="font-mono text-xs">{code.password}</TableCell>
                      <TableCell className="text-xs">{code.server_url}</TableCell>
                      <TableCell className="text-xs">{code.assigned_to_email || "—"}</TableCell>
                      <TableCell className="text-xs">{formatDate(code.assigned_at)}</TableCell>
                      <TableCell className="text-xs">{formatDate(code.expires_at)}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {(code.status === "active" || code.status === "expired") && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleRecycle(code.id)}
                              title="Recycler (remettre disponible)"
                            >
                              <RotateCw className="w-3 h-3" />
                            </Button>
                          )}
                          {code.status === "available" && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDelete(code.id)}
                              title="Supprimer"
                              className="text-red-400 hover:text-red-300"
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminTestCodesSection;
